import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { BackButton, Button, Center, CompendiumPanel, Grid, Loader, Modal, TextInput, useDisclosure, YesCancelModal } from '@labsavvyapp/components'
import { useOrdersetSettingsPageBusinessLogic } from '../../../logic/context'
import { useLocation } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { OrdersetCategory } from './components'
import formatterPrice from '../../../../../../utils/formatter-price'

const ComponentLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: 15px;
  width: 100%;
`

export interface CompendiumPanelWithGuid {
  id: string
  category_id: string
  panels: CompendiumPanel[]
}

export const EditOrderset: React.FC = () => {
  const location = useLocation()

  const [visible, { open: openConfirmDeleteOrdersetModal, close: closeConfirmDeleteOrdersetModal, toggle: toggleConfirmDeleteOrdersetModal }] =
    useDisclosure(false)

  const pageLogic = useOrdersetSettingsPageBusinessLogic()
  const editOrdersetForm = pageLogic?.editOrdersetForm ?? null
  const userSelectedOrdersetData = pageLogic?.userSelectedOrdersetData ?? null
  const userSelectedOrdersetDataLoading = pageLogic?.userSelectedOrdersetDataLoading ?? false
  const isOrdersetPrivate = userSelectedOrdersetData?.is_private ?? false
  const defaultCategoryData = pageLogic?.defaultCategoryData ?? null
  const defaultCategoryDataId = defaultCategoryData?.configs[0]?.value ?? '61ddcd93e87bef00179db0c4'
  const totalEditOrdersetClientPrice = pageLogic?.totalEditOrdersetClientPrice ?? 0
  const totalEditOrdersetPatientPrice = pageLogic?.totalEditOrdersetPatientPrice ?? 0

  const [packageCategoryPanels, setPackageCategoryPanels] = useState<CompendiumPanelWithGuid[]>([])

  useEffect(() => {
    editOrdersetForm.setFieldValue(
      'categories',
      packageCategoryPanels.map((category) => ({
        category_id: defaultCategoryDataId,
        panels: category.panels,
      })),
    )
  }, [packageCategoryPanels])

  useEffect(() => {
    editOrdersetForm.setFieldValue('name', userSelectedOrdersetData?.name)
  }, [])

  useEffect(() => {
    if (!userSelectedOrdersetData) {
      const id = location.pathname.split('/').pop()
      if (id) pageLogic?.handleUserSelectedOrdersetId(id)
    } else if (userSelectedOrdersetData) {
      const categoryPanels = userSelectedOrdersetData.categories?.map(
        (category) =>
        ({
          id: uuidv4(),
          category_id: defaultCategoryDataId,
          panels: category?.panels,
        } as unknown as CompendiumPanelWithGuid),
      )

      setPackageCategoryPanels(categoryPanels)
    }
  }, [userSelectedOrdersetData])

  const handleBackToOrdersetListPageClick = () => {
    pageLogic?.goToOrdersetListPage()
  }

  const handleDeleteOrdersetClick = () => {
    pageLogic?.deleteOrderset()
  }

  if (userSelectedOrdersetDataLoading) {
    return (
      <Center>
        <Loader color="violet" size="md" />
      </Center>
    )
  }

  return (
    <ComponentLayout>
      <BackButton onClick={handleBackToOrdersetListPageClick} text="Back to Orderset List" />
      <h1>Edit Orderset</h1>
      <Grid mt={15}>
        <Grid.Col md={12} lg={4}>
          <TextInput
            label="Orderset Name"
            placeholder="Orderset Name"
            withAsterisk
            disabled={!isOrdersetPrivate}
            {...editOrdersetForm.getInputProps('name')}
          />
        </Grid.Col>

        {isOrdersetPrivate && (
          <>
            {packageCategoryPanels.length > 0 && (
              <Grid.Col md={12} lg={4}>
                <TextInput label="Orderset Client Bill" type="number" value={totalEditOrdersetClientPrice?.toFixed(2)} icon={'$'} />
              </Grid.Col>
            )}

            {packageCategoryPanels.length > 0 && (
              <Grid.Col md={12} lg={4}>
                <TextInput label="Orderset Patient Bill" type="number" value={totalEditOrdersetPatientPrice?.toFixed(2)} icon={'$'} />
              </Grid.Col>
            )}

            <Grid.Col md={4} lg={2} mt={35}>
              <Button radius="xl" size="md" uppercase variant="outline" onClick={openConfirmDeleteOrdersetModal} disabled={!isOrdersetPrivate}>
                Delete Orderset
              </Button>
            </Grid.Col>
          </>
        )}

        <Grid.Col lg={12}>
          {packageCategoryPanels?.map((packageCategoryPanel) => (
            <OrdersetCategory
              category={packageCategoryPanel}
              allCategories={packageCategoryPanels}
              key={packageCategoryPanel?.id}
              handleOrdersetCategoryChange={setPackageCategoryPanels}
            />
          ))}
        </Grid.Col>
      </Grid>
      <YesCancelModal
        onCancelClick={closeConfirmDeleteOrdersetModal}
        onClose={closeConfirmDeleteOrdersetModal}
        onYesClick={handleDeleteOrdersetClick}
        subTitle="Are you sure you want to delete this orderset?"
        title="Orderset Deletion Confirmation"
        yesButtonLabel="Delete Orderset"
        children={null}
        __staticSelector=""
        opened={visible}
      />
    </ComponentLayout>
  )
}
